<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useConfiguratorStore } from '@/stores/useConfiguratorStore'
import { useCatalogLinksStore } from '@/stores/useCatalogLinksStore'

const configuratorStore = useConfiguratorStore()
const catalogLinksStore = useCatalogLinksStore()
const showModal = ref(false)

const catalogues = computed(() => catalogLinksStore.sorted)

// Load once on mount; the store caches across openings of the modal.
onMounted(() => {
  catalogLinksStore.fetchLinks()
})

function closeModal() {
  showModal.value = false
}
</script>

<template>
  <!-- Floating Button -->
  <button
    v-show="!configuratorStore.isOpen"
    type="button"
    class="catalogue-button"
    aria-label="Catalogues fournisseurs"
    @click="showModal = true"
  >
    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  </button>

  <!-- Modal -->
  <Teleport to="body">
    <div
      v-if="showModal"
      class="fixed inset-0 z-[950] flex items-end sm:items-center justify-center"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black/40"
        @click="showModal = false"
      />

      <!-- Panel -->
      <div
        class="relative w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl shadow-xl
               max-h-[80vh] overflow-hidden animate-slide-up"
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 class="text-base font-semibold text-gray-900">
            Catalogues fournisseurs
          </h2>
          <button
            type="button"
            class="p-1.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
            @click="showModal = false"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Catalogue List -->
        <!-- Real <a target="_blank"> anchors so Android Chrome PWA hands off
             to the system browser instead of swallowing the URL in the
             PWA's own webview (window.open() is unreliable in standalone). -->
        <div class="p-3 space-y-1.5">
          <a
            v-for="cat in catalogues"
            :key="cat.id"
            :href="cat.url"
            target="_blank"
            rel="noopener noreferrer"
            class="w-full flex items-center gap-3 px-4 py-3 rounded-xl
                   hover:bg-gray-50 transition-colors text-left group no-underline"
            @click="closeModal"
          >
            <span
              class="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold"
              :class="cat.color"
            >
              {{ cat.icon }}
            </span>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 group-hover:text-vfr-red transition-colors">
                {{ cat.name }}
              </p>
              <p class="text-xs text-gray-400 truncate">
                {{ cat.url.replace(/^https?:\/\//, '').split('/')[0] }}
              </p>
            </div>
            <svg class="w-4 h-4 text-gray-300 group-hover:text-vfr-red transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.catalogue-button {
  position: fixed;
  bottom: 5rem;
  right: 1rem;
  z-index: 899;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: #2563eb;
  color: white;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: background 0.2s ease, box-shadow 0.2s ease;
}

.catalogue-button:hover {
  background: #1d4ed8;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.catalogue-button:focus-visible {
  outline: 2px solid #0066CC;
  outline-offset: 2px;
}

.animate-slide-up {
  animation: slideUp 0.2s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(1rem);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
